import { enableProdMode, ErrorHandler, importProvidersFrom, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { Integration } from '@sentry/types';
import { APP_CONFIG, setConfig } from '@cco-apps/cco-frontend';
import { jwtOptionsFactory } from '@app/app.module';
import { environment } from '@env/environment';
import { AppComponent } from '@app/app.component';
import { ServiceWorkerModule } from '@angular/service-worker';
import { Auth2Service } from '@core/auth2/services/auth2.service';
import { entityConfig } from '@app/store/entity-metadata';
import { RouterEffects } from '@app/core/store/effects/router.effects';
import { TruckLiveDataEffects } from '@app/main/trucks/store/truck-live-data.effects';
import { MediaEffects } from '@app/core/store/effects/media.effects';
import { Auth2Effects } from '@core/auth2/store/auth2.effects';
import { EffectsModule, provideEffects } from '@ngrx/effects';
import { NgrxStoreIdbModule, NgrxStoreIdbTenantResolver } from '@core/ngrx/ngrx-store-idb';
import { StoreIdbTenantResolverService } from '@core/user-tenant/store-idb-tenant-resolver.service';
import { initTenantContext, LEGACY_DB_NAME, resolveBootstrapTenantId, tenantDbName } from '@core/user-tenant/tenant-context';
import { claimTabIdentity, holdTabIdentityLock } from '@core/user-tenant/tab-identity';
import { provideState, provideStore } from '@ngrx/store';
import { AppRoutingModule } from '@app/app-routing.module';
import { CoreModule } from '@app/core';
import { E2eModule } from '@app/e2e/e2e.module';
import { bootstrapApplication, BrowserModule } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { getReducers, metaReducers, REDUCER_TOKEN } from '@app/store/reducers';
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip';
import { provideMaterialSymbols } from '@app/shared/icons/material-symbols.provider';
import { CustomRouterStateSerializer } from '@app/core/router';
import { NavigationActionTiming, RouterStateSerializer, StoreRouterConnectingModule } from '@ngrx/router-store';
import { JWT_OPTIONS, JwtModule } from '@app/modules/angular-jwt/angular-jwt.module';
import { TokenService } from '@core/services/token.service';
import { HttpErrorResponse } from '@angular/common/http';
import { CcoEntityCacheDataService } from '@app/data/cco-entity-cache-data.service';
import { CcoDefaultDataServiceFactory } from '@app/data/cco-default-data-service';
import { CcoEntityCollectionReducerMethodsFactory } from '@app/data/ngrx-data';
import { CcoHttpUrlGenerator } from '@app/store/cco-http-url-generator';
import { CcoPersistenceResultHandler } from '@app/data/cco-persistence-result-handler';
import {
  DefaultDataServiceFactory,
  EntityCacheDataService,
  EntityCollectionReducerMethodsFactory,
  EntityDataModule,
  PersistenceResultHandler,
} from '@ngrx/data';
import { MyErrorHandler } from '@app/shared/utils/my-error-handler';
import { EnterKeyGuardService } from '@app/shared/services/enter-key-guard.service';
import { userTenantFeature } from '@core/user-tenant/user-tenant.reducer';
import * as userTenantEffects from '@core/user-tenant/user-tenant.effects';
import * as globalFilterEffects from '@app/store/global-filter.effects';
import { ngErrorExtractor, WrappingSentryErrorHandler } from '@app/shared/utils/wrapping-sentry-error-handler';
import {
  holdAppTabLock,
  LOCAL_DATA_RESET_ROUTE,
  LocalDataResetScope,
  performLocalDataReset,
  SERVICE_WORKER_RESET_ROUTES,
  unregisterAllServiceWorkers,
} from '@app/modules/reset-state/reset-local-data';
import { renderBootstrapFailureCard, renderResetStatusCard } from '@app/modules/reset-state/reset-status-card';
import { ConfigUnavailableError, loadAppConfig } from '@core/config/load-app-config';
import { clearEdgeAuthGateMarker, recoverThroughEdgeAuthGateIfClosed } from '@core/auth2/edge-auth-gate';
import {
  HTTP_STATUS_TEXT,
  makeUrlGeneric,
  stripQueryParams,
  upsertExceptionTypeValue,
} from '@app/shared/utils/sentry.utils';

interface HasCcoConfigFile {
  ccoConfigFile: string;
}

// The local-data reset must run INSTEAD of the app, not inside it: a running
// app instance wins the single-tab ngrx-store-idb master election, rehydrates
// the old persisted `State` record, and writes it right back — resurrecting
// the slices the reset just cleared.
const requestedRoute = window.location.pathname.replace(/^\//, '');
const isServiceWorkerResetRoute = SERVICE_WORKER_RESET_ROUTES.includes(requestedRoute);

if (requestedRoute === LOCAL_DATA_RESET_ROUTE || isServiceWorkerResetRoute) {
  const scope: LocalDataResetScope =
    isServiceWorkerResetRoute
      || new URLSearchParams(window.location.search).get('scope') === 'service-worker'
      ? 'service-worker'
      : 'data';
  const statusCard = scope === 'service-worker'
    ? renderResetStatusCard({
      title: 'Resetting Service Worker',
      workingMessage: 'Unregistering service workers and clearing cached application files. You will be redirected shortly…',
    })
    : renderResetStatusCard();
  performLocalDataReset(openTabs => statusCard.setOpenTabs(openTabs), scope)
    .finally(() => window.location.replace('/app'));
} else {
  // Wait for the shared app-tab lock before booting so a reset in another
  // tab (holding it exclusively) finishes before this tab opens IndexedDB.
  // Then publish this tab's identity — other tabs read it to tell whether the
  // tab that last chose a tenant is still open — and settle which tenant this
  // tab runs as, which every synchronous bootstrap-time reader of
  // resolveBootstrapTenantId() depends on having happened first.
  holdAppTabLock()
    .then(claimTabIdentity)
    .then(async (tabId) => {
      await holdTabIdentityLock(tabId);
      await initTenantContext();
    })
    // Tenant resolution is a preference, never a gate: if the lock or storage
    // machinery fails, boot anyway and let the reducer pick the home tenant.
    .catch(() => undefined)
    .then(bootstrapCcoApp);
}

function bootstrapCcoApp(): void {
loadAppConfig((window as unknown as HasCcoConfigFile).ccoConfigFile)
  .then((config) => {
    // The config loaded, so the auth gate let us through and the bypass marker
    // that may have carried us here has done its job.
    clearEdgeAuthGateMarker();

    // An environment that turns the service worker off keeps any worker a
    // previous deploy installed until it is explicitly unregistered -- the flag
    // only governs whether a new one is registered.
    const serviceWorkerEnabled = environment.production && config.enableServiceWorker !== false;
    if (!serviceWorkerEnabled) {
      void unregisterAllServiceWorkers();
    }

    const errorHandlerFactory = () => {
      if (config.sentry.enabled === true) {
        return new WrappingSentryErrorHandler({
          extractor: ngErrorExtractor,
          showDialog: config.sentry.showReportDialog,
        });
      } else {
        return new MyErrorHandler();
      }
    };

    setConfig(config);

    if (window.location.origin === 'https://dev.concretecloudops.io')
      window.location.href = 'https://app.concretecloudops.io';

    // Sentry.io
    if (config.sentry.enabled == null || config.sentry.enabled === true) {
      const integrations: Integration[] = [
        Sentry.breadcrumbsIntegration({
          console: false,
        }),
        Sentry.extraErrorDataIntegration({
          // Normalize depth needs to be at least 1 higher
          // See https://github.com/getsentry/sentry-javascript/issues/2539
          depth: 4,
        }),
      ];
      if (config.sentry.replay.enabled)
        integrations.push(Sentry.replayIntegration());
      if (config.sentry.tracing.enabled)
        integrations.push(Sentry.browserTracingIntegration());

      Sentry.init({
        beforeSend: (event, hint) => {
          const error = hint.originalException;

          if (error instanceof HttpErrorResponse) {
            const genericUrl = makeUrlGeneric(config, stripQueryParams(error.url));

            const type = (error.status === 400 && typeof error.error?.error === 'string')
              ? error.error.error
              : 'HttpErrorResponse';
            const reasonPhrase = (error?.error?.error && typeof error.error.error === 'string')
              ? ` ${error.error.error}`
              : error.statusText
                ? ` ${error.statusText}`
                : HTTP_STATUS_TEXT[error.status] ?? '';
            const value = (error.error?.error && error.error?.message)
              ? `HTTP ${error.status}${reasonPhrase}: ${genericUrl}: ${error.error.message}`
              : `HTTP ${error.status}${reasonPhrase}: ${genericUrl}`;
            upsertExceptionTypeValue(event, value, type);
            delete event.exception.values[0].stacktrace;
            if (event.fingerprint == null)
              event.fingerprint = [];
            event.fingerprint.push('HttpErrorResponse');
            event.fingerprint.push(genericUrl);
            event.fingerprint.push(`http-status-${error.status}`);
          }

          return event;
        },
        dsn: config.sentry.dsn,
        environment: config.sentry.environment,
        integrations,
        normalizeDepth: 5,
        release: config.sentry.release,
        replaysOnErrorSampleRate: config.sentry.replay.errorSampleRate ?? 1.0,
        sendDefaultPii: true,
        tracePropagationTargets: [
          /^\//,
          config.apiBaseUrl,
          config.cloudApiBaseUrl,
          config.mailerBaseUrl,
          config.pdfExporterBaseUrl,
        ],
        tracesSampleRate: config.sentry.tracing.sampleRate ?? (environment.production ? 0.02 : 1.0),
        transport: Sentry.makeBrowserOfflineTransport(),
        tunnel: config.sentry.tunnel,
      });
    }

    if (environment.production) {
      enableProdMode();
    }

    // The pre-split 'cco' database mixed all tenants' state; state now lives in
    // per-tenant databases. Fire-and-forget: if the delete is blocked by a tab
    // still running the old version it will be retried on the next startup.
    indexedDB.deleteDatabase(LEGACY_DB_NAME);

    bootstrapApplication(AppComponent, {
      providers: [
        // Angular 21+ defaults to zoneless change detection; this codebase predates that and
        // relies pervasively on zone-driven rendering (subscribe-and-assign, dialog callbacks,
        // timers), so keep zone-based change detection until the deliberate zoneless
        // migration tracked in #2567.
        provideZoneChangeDetection(),
        provideMaterialSymbols(),
        {
          provide: APP_CONFIG,
          useValue: config,
        },
        provideStore(REDUCER_TOKEN, {
          metaReducers,
          runtimeChecks: {
            strictActionImmutability: false,
            strictStateImmutability: false,
          },
        }),
        provideState(userTenantFeature),
        provideEffects(userTenantEffects, globalFilterEffects),
        {
          provide: NgrxStoreIdbTenantResolver,
          useClass: StoreIdbTenantResolverService,
        },
        ...environment.providers,
        importProvidersFrom(
          BrowserModule,
          E2eModule,
          // AppRoutingModule must register its routes (`unauthorized`,
          // `reset-local-data`) before CoreRoutingModule's `**` not-found
          // route, or the wildcard shadows them.
          AppRoutingModule,
          CoreModule,
          NgrxStoreIdbModule.forRoot({
            idb: {
              dbNameForTenant: tenantDbName,
              storeName: 'ngrx',
              bootstrapTenantId: resolveBootstrapTenantId,
            },
            rehydrate: true,
            keys: [ 'auth2', 'entityCache', 'iotThings', 'selects', 'logger' ],
            // entityCache holds every @ngrx/data collection; persisting them
            // as one record each keeps a single upsert from re-serialising
            // all of them.
            perEntryKeys: [ 'entityCache' ],
            debugInfo: false,
          }), StoreRouterConnectingModule.forRoot({
            stateKey: 'router',
            navigationActionTiming: NavigationActionTiming.PostActivation,
          }),
          EffectsModule.forRoot([ Auth2Effects, MediaEffects, TruckLiveDataEffects, RouterEffects ]), EntityDataModule.forRoot(entityConfig), JwtModule.forRoot({
            jwtOptionsProvider: {
              provide: JWT_OPTIONS,
              useFactory: jwtOptionsFactory,
              deps: [ TokenService, Auth2Service ],
            },
          }), ServiceWorkerModule.register('ngsw-worker.js', {
            enabled: serviceWorkerEnabled,
            // Angular's default `registerWhenStable:30000` never sees stability here: the
            // store-idb heartbeat, the global filter recompute and the stomp heartbeat all
            // keep the zone busy for the app's lifetime, so registration always fell through
            // to the 30s timeout.  Delay by a fixed interval instead, which keeps the
            // default's intent -- stay off the critical path of the initial load -- without
            // depending on a signal this app cannot emit until the zoneless migration (#2567).
            registrationStrategy: 'registerWithDelay:5000',
          })),
        {
          provide: PersistenceResultHandler,
          useClass: CcoPersistenceResultHandler,
        },
        // { provide: HttpUrlGenerator, useClass: CcoHttpUrlGenerator },
        CcoHttpUrlGenerator,
        {
          provide: EntityCollectionReducerMethodsFactory,
          useClass: CcoEntityCollectionReducerMethodsFactory,
        },
        {
          provide: DefaultDataServiceFactory,
          useClass: CcoDefaultDataServiceFactory,
        },
        {
          provide: EntityCacheDataService,
          useClass: CcoEntityCacheDataService,
        },
        {
          provide: ErrorHandler,
          useFactory: errorHandlerFactory,
        },
        { provide: RouterStateSerializer, useClass: CustomRouterStateSerializer },
        // This is to disable tooltips on mobile devices in order to allow scrolling
        // within elements with tooltips.
        // @see https://github.com/angular/components/issues/4892#issuecomment-649108017
        {
          provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: {
            touchGestures: 'off',
          },
        },
        {
          provide: REDUCER_TOKEN,
          useFactory: getReducers,
        },
        {
          provide: Sentry.TraceService,
          deps: [ Router ],
        },
        provideAppInitializer(() => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars,@typescript-eslint/no-empty-function
          const initializerFn = (_ => () => {
            })(inject(Sentry.TraceService));
          return initializerFn();
        }),
        provideAppInitializer(() => inject(EnterKeyGuardService).install()),
      ],
    })
      .catch((err) => {
        console.error('An unhandled error occurred during platform bootstrapping', err);
        Sentry.captureException(err);
      });
  })
  .catch(err => handleBootstrapFailure(err));
}

/**
 * Last resort for a boot that never got off the ground.
 *
 * A missing runtime config is usually the edge auth gate (the oauth2-proxy in
 * front of the PR preview environments) answering with a redirect to the
 * identity provider — invisible here, because the service worker turns that
 * redirect into a synthetic 504. Reloading past the service worker lets the
 * browser follow it and sign the user back in. Anything else, or a reload that
 * has already been tried, gets a card instead of an endless boot spinner.
 */
async function handleBootstrapFailure(error: unknown): Promise<void> {
  console.error('An unhandled error occurred in main()', error);

  if (!(error instanceof ConfigUnavailableError)) {
    return;
  }

  if (await recoverThroughEdgeAuthGateIfClosed()) {
    return;
  }

  renderBootstrapFailureCard(
    'Concrete CloudOps could not load its configuration. Please reload the page, '
    + 'and contact support if the problem persists.',
  );
}
